import React, { useCallback, useMemo, useState } from "react";
import { Grid, IconButton, Icon } from "@mui/material";
import FormField from "components/FormField/FormField";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { useFormContext } from "react-hook-form";
import CurrencyFormField from "components/CurrencyFormField/CurrencyFormField";
import currencyEndAdornment from "constants/currencyEndAdornment";
import useDeleteProposalMaterialNoPlant from "hooks/proposals/useDeleteProposalMaterialNoPlant";
import useConfirmationDialog from "hooks/useConfirmationDialog";
import ActionConfirmDialog from "components/ActionConfirmDialog/ActionConfirmDialog";
import formatCentsToUSD from "utils/money/formatCentsToUSD";

interface MaterialsNoPlantSectionProps {
  proposalMaterialNoPlants: Array<{
    id: string;
    name: string;
    percentage: number;
    rate: number;
    quantity: number;
    quantityOverride?: number | null;
    cost: string | number;
  }>;
  inViewMode?: boolean;
}

export default function MaterialsNoPlantSection({
  proposalMaterialNoPlants,
  inViewMode = false,
}: MaterialsNoPlantSectionProps) {
  const { control, register, getValues, setValue, watch } = useFormContext();
  const [deleteProposalMaterialNoPlant, { loading: deleteLoading }] =
    useDeleteProposalMaterialNoPlant();
  const [materialToDelete, setMaterialToDelete] = useState<string | null>(null);
  const materialNoPlantValues = watch("materialNoPlants");
  const materialNoPlantsById = useMemo(
    () =>
      proposalMaterialNoPlants.reduce(
        (acc, item) => {
          acc[item.id] = item;
          return acc;
        },
        {} as Record<string, MaterialsNoPlantSectionProps["proposalMaterialNoPlants"][number]>
      ),
    [proposalMaterialNoPlants]
  );
  const rows = Array.isArray(materialNoPlantValues)
    ? materialNoPlantValues
    : proposalMaterialNoPlants;

  const acceptFn = useCallback(async (): Promise<boolean> => {
    if (!materialToDelete) return false;

    try {
      await deleteProposalMaterialNoPlant({
        variables: { id: materialToDelete },
      });
      const current = getValues("materialNoPlants") ?? [];
      setValue(
        "materialNoPlants",
        current.filter((item) => item?.id !== materialToDelete),
        { shouldDirty: true }
      );
      setMaterialToDelete(null);
      return true;
    } catch (error) {
      return false;
    }
  }, [deleteProposalMaterialNoPlant, getValues, materialToDelete, setValue]);

  const cancelFn = useCallback(async () => {
    return true;
  }, []);

  const { open, setOpen, onAccept, onCancel } = useConfirmationDialog({
    acceptFn,
    cancelFn,
  });

  const handleDeleteMaterial = useCallback(
    (materialId: string) => {
      setMaterialToDelete(materialId);
      setOpen(true);
    },
    [setOpen]
  );

  if (!rows || rows.length === 0) {
    return null;
  }

  return (
    <MDBox>
      {rows.map((materialNoPlant, index) => {
        const persistedMaterial = materialNoPlant?.id ? materialNoPlantsById[materialNoPlant.id] : null;

        return (
          <Grid container spacing={1} key={materialNoPlant.id} mb={2}>
            <input type="hidden" {...register(`materialNoPlants.${index}.id`)} />
            <Grid item xs={12} md={2.4}>
              <FormField
                disabled={inViewMode}
                align="left"
                label="Material Name"
                {...register(`materialNoPlants.${index}.name`)}
              />
            </Grid>
            <Grid item xs={12} md={2.4}>
              <FormField
                disabled={inViewMode}
                align="right"
                label="Percentage (%)"
                type="number"
                inputProps={{ min: 0, max: 100, step: 0.01 }}
                {...register(`materialNoPlants.${index}.percentage`, {
                  valueAsNumber: true,
                })}
              />
            </Grid>
            <Grid item xs={12} md={2.4}>
              <FormField
                disabled={inViewMode}
                align="right"
                label="Quantity"
                type="number"
                placeholder={persistedMaterial?.quantity}
                inputProps={{ min: 1, step: 1 }}
                {...register(`materialNoPlants.${index}.quantityOverride`, {
                  valueAsNumber: true,
                })}
              />
            </Grid>
            <Grid item xs={12} md={2.4}>
              <CurrencyFormField
                disabled={inViewMode}
                label="Rate"
                {...currencyEndAdornment}
                control={control}
                name={`materialNoPlants.${index}.rate`}
                sendEmptyValuesAsNull
              />
            </Grid>
            <Grid item xs={12} md={1.9}>
              <MDBox
                display="flex"
                alignItems="end"
                justifyContent="flex-end"
                height="100%"
                pb="8px"
              >
                <MDTypography variant="body2" sx={{ fontSize: "0.9em", fontWeight: "bold" }}>
                  {formatCentsToUSD(persistedMaterial?.cost || 0)}
                </MDTypography>
              </MDBox>
            </Grid>
            {!inViewMode && (
              <Grid item xs={12} md={0.5}>
                <MDBox
                  display="flex"
                  alignItems="end"
                  justifyContent="flex-end"
                  height="100%"
                  pb="8px"
                >
                  <IconButton
                    color="error"
                    onClick={() => handleDeleteMaterial(materialNoPlant.id)}
                    disabled={deleteLoading}
                    size="small"
                  >
                    <Icon>delete</Icon>
                  </IconButton>
                </MDBox>
              </Grid>
            )}
          </Grid>
        );
      })}

      <ActionConfirmDialog
        open={open}
        setOpen={setOpen}
        handleAccept={onAccept}
        handleCancel={onCancel}
        title="Delete Material"
        content="Are you sure you want to delete this material from the proposal product? This action cannot be undone."
        acceptText="Delete Material"
        cancelText="Cancel"
      />
    </MDBox>
  );
}
